import { useCallback, useEffect, useId, useRef, useState } from 'react';
import { MAX_CAPTION, MAX_LOCATION, type PhotoText, type UploadResult } from '@mantelpiece/shared';
import { ACCEPTED_TYPES, preparePhoto, readLocationHint, type PreparedPhoto } from '../lib/image';

type ItemState = 'preparing' | 'uploading' | 'done' | 'duplicate' | 'waiting' | 'failed';

interface QueueItem {
  readonly key: string;
  readonly filename: string;
  state: ItemState;
  previewUrl: string | null;
  error: string | null;
}

/** Phones fall over if you decode eight 12-megapixel photos at once. */
const CONCURRENCY = 3;

const STATUS_TEXT: Record<ItemState, string> = {
  preparing: 'Resizing',
  uploading: 'Sending',
  done: 'On the TV',
  duplicate: 'Already there',
  waiting: 'Waiting for approval',
  failed: 'Failed',
};

interface Props {
  /**
   * How to send one prepared photo. The panel does not know whether it is
   * posting as a signed-in member or through a frame's public link — those are
   * two different endpoints with the same queue, progress list and failure
   * modes, and only the caller has to care which is which.
   */
  readonly upload: (photo: PreparedPhoto, text: PhotoText) => Promise<UploadResult>;
  /** Fired once a whole batch has finished, so a caller can refresh its list. */
  readonly onSettled?: (() => void) | undefined;
  /** Replaces the picker's headline when the destination needs saying. */
  readonly headline?: string | undefined;
  /** Disables the picker — nowhere selected to send to yet, typically. */
  readonly disabled?: boolean | undefined;
  /**
   * What a finished upload is called. Defaults to "On the TV", which is true of
   * a frame and a lie about an album that is not on one yet.
   */
  readonly doneText?: string | undefined;
}

/** The picker, the resize queue and its progress list. */
export const UploadPanel = ({
  upload,
  onSettled,
  headline,
  disabled = false,
  doneText,
}: Props) => {
  const [items, setItems] = useState<readonly QueueItem[]>([]);
  const [caption, setCaption] = useState('');
  const [location, setLocation] = useState('');
  // Coordinates found in the batch, offered rather than filled in. Cleared once
  // it has been taken up, so the chip does not linger next to its own answer.
  const [locationHint, setLocationHint] = useState<string | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const textRef = useRef<PhotoText>({ caption: null, location: null });
  // Callers pass an inline arrow, so this changes identity every render. Held in
  // a ref rather than a dependency so the worker pool is not rebuilt underneath
  // an upload that is already in flight.
  const uploadRef = useRef(upload);
  const inputId = useId();

  textRef.current = {
    caption: caption.trim() || null,
    location: location.trim() || null,
  };
  uploadRef.current = upload;

  // Object URLs are not garbage collected on their own.
  useEffect(() => {
    return () => {
      for (const item of items) if (item.previewUrl) URL.revokeObjectURL(item.previewUrl);
    };
    // Intentionally cleanup-on-unmount only; per-item revocation happens on clear.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const patch = useCallback((key: string, changes: Partial<QueueItem>) => {
    setItems((current) =>
      current.map((item) => (item.key === key ? { ...item, ...changes } : item)),
    );
  }, []);

  const processOne = useCallback(
    async (key: string, file: File) => {
      try {
        const prepared = await preparePhoto(file);
        patch(key, { state: 'uploading', previewUrl: prepared.previewUrl });

        const result = await uploadRef.current(prepared, textRef.current);
        patch(key, {
          state: result.duplicate ? 'duplicate' : result.accepted ? 'done' : 'waiting',
        });
      } catch (error) {
        patch(key, {
          state: 'failed',
          error: error instanceof Error ? error.message : 'Could not send that one.',
        });
      }
    },
    [patch],
  );

  const settledRef = useRef(onSettled);
  settledRef.current = onSettled;

  const enqueue = useCallback(
    async (files: readonly File[]) => {
      const images = files.filter((file) => file.type.startsWith('image/'));
      if (!images.length) return;

      // Before anything is resized or sent, so the offer is in front of the
      // sender while it can still reach the whole batch.
      void readLocationHint(images).then((hint) => {
        if (hint) setLocationHint((current) => current ?? hint);
      });

      const queued: QueueItem[] = images.map((file, index) => ({
        key: `${Date.now()}-${index}-${file.name}`,
        filename: file.name,
        state: 'preparing',
        previewUrl: null,
        error: null,
      }));
      setItems((current) => [...current, ...queued]);

      // Fixed-size worker pool over a shared cursor.
      let cursor = 0;
      const workers = Array.from({ length: Math.min(CONCURRENCY, images.length) }, async () => {
        while (cursor < images.length) {
          const index = cursor;
          cursor += 1;
          await processOne(queued[index]!.key, images[index]!);
        }
      });
      await Promise.all(workers);
      settledRef.current?.();
    },
    [processOne],
  );

  const onDrop = (event: React.DragEvent) => {
    event.preventDefault();
    setDragOver(false);
    void enqueue([...event.dataTransfer.files]);
  };

  const clearFinished = () => {
    setItems((current) => {
      const keep: QueueItem[] = [];
      for (const item of current) {
        if (item.state === 'preparing' || item.state === 'uploading' || item.state === 'failed') {
          keep.push(item);
        } else if (item.previewUrl) {
          URL.revokeObjectURL(item.previewUrl);
        }
      }
      return keep;
    });
  };

  const busy = items.some((i) => i.state === 'preparing' || i.state === 'uploading');
  const finished = items.filter(
    (i) => i.state === 'done' || i.state === 'duplicate' || i.state === 'waiting',
  ).length;

  return (
    <>
      <div className="stack">
        <label
          className="easel"
          htmlFor={inputId}
          data-over={dragOver}
          data-disabled={disabled}
          onDragOver={(event) => {
            if (disabled) return;
            event.preventDefault();
            setDragOver(true);
          }}
          onDragLeave={() => setDragOver(false)}
          onDrop={disabled ? (event) => event.preventDefault() : onDrop}
        >
          <span className="easel-headline">{headline ?? 'Choose photos'}</span>
          <span className="note">or drag them onto this square</span>
          <input
            ref={inputRef}
            id={inputId}
            type="file"
            accept={ACCEPTED_TYPES}
            multiple
            disabled={disabled}
            onChange={(event) => {
              void enqueue([...(event.target.files ?? [])]);
              event.target.value = '';
            }}
          />
        </label>

        <label className="field">
          <span>Caption for this batch (optional)</span>
          <input
            className="input"
            value={caption}
            maxLength={MAX_CAPTION}
            placeholder="Beach, August"
            onChange={(event) => setCaption(event.target.value)}
          />
        </label>

        <label className="field">
          <span>Where it was taken (optional)</span>
          <input
            className="input"
            value={location}
            maxLength={MAX_LOCATION}
            placeholder="Brighton beach"
            onChange={(event) => setLocation(event.target.value)}
          />
        </label>

        {locationHint && !location.trim() && (
          <p className="note">
            These photos know where they were taken.{' '}
            <button
              type="button"
              className="button button-quiet button-small"
              onClick={() => {
                setLocation(locationHint);
                setLocationHint(null);
              }}
            >
              Use {locationHint}
            </button>
          </p>
        )}
      </div>

      {items.length > 0 && (
        <section>
          <div className="section-head">
            <p className="eyebrow">
              Batch · {finished} of {items.length} sent
            </p>
            <button
              type="button"
              className="button button-quiet button-small"
              onClick={clearFinished}
              disabled={busy && finished === 0}
            >
              Clear sent
            </button>
          </div>

          <p className="visually-hidden" role="status" aria-live="polite">
            {busy ? `Sending photos, ${finished} of ${items.length} done.` : 'All photos sent.'}
          </p>

          <ul className="sheet">
            {items.map((item, index) => (
              <li
                key={item.key}
                className="print"
                data-state={
                  item.state === 'duplicate' || item.state === 'waiting' ? 'done' : item.state
                }
              >
                <div className="print-image">
                  {item.previewUrl ? (
                    <img src={item.previewUrl} alt="" />
                  ) : (
                    <span className="visually-hidden">Preparing {item.filename}</span>
                  )}
                </div>
                <p className="print-caption">
                  <span className="print-frame">{String(index + 1).padStart(2, '0')}</span>
                  <span className="print-status">
                    {item.state === 'done' ? doneText ?? STATUS_TEXT.done : STATUS_TEXT[item.state]}
                  </span>
                </p>
                {item.error && <p className="note">{item.error}</p>}
              </li>
            ))}
          </ul>
        </section>
      )}
    </>
  );
};
